import { ExpandableContent } from '@ben/orion/expandable-content';
import { InformationCard } from '@ben/orion/information-card';
import { TypographyVariant } from '@ben/orion/typography';
import { Group, Stack, Text } from '@mantine/core';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { domainLabel } from './domains';
import { Metadata } from './shared';
import { StatusTag } from './status';
import type { WorkspaceStatus } from './status';
import { useGroupDetail } from './use-group-detail';

const RUNS_PREVIEW = 5;

/** Group metadata (type, benefit, programme, intent) and run history. */
export const GroupSidebar = () => {
  const { group, runs } = useGroupDetail();
  if (!group) return null;

  return (
    <Stack gap="md">
      <InformationCard
        title="Details"
        titleVariant={TypographyVariant.h3}
        gap={2}
      >
        <Metadata>
          <dt>Type</dt>
          <dd>{groupType(group.entity, group.domain)}</dd>
          <dt>Benefit</dt>
          <dd>
            {group.benefit ? (
              <Link to={`/benefits/${group.benefit.id}`}>
                {group.benefit.name}
              </Link>
            ) : (
              'Not built yet'
            )}
          </dd>
          <dt>Programme</dt>
          <dd>
            {group.programmes.length > 0
              ? group.programmes.map((p) => p.name).join(', ')
              : 'None'}
          </dd>
        </Metadata>
        {group.intent ? <IntentText>{group.intent}</IntentText> : null}
      </InformationCard>

      <RunsCard runs={runs} />
    </Stack>
  );
};

const RunsCard = ({
  runs,
}: {
  runs: ReturnType<typeof useGroupDetail>['runs'];
}) => {
  const hasMore = runs.length > RUNS_PREVIEW;

  return (
    <InformationCard title="Runs" titleVariant={TypographyVariant.h3} gap={2}>
      {runs.length === 0 ? (
        <Text size="sm" c="dimmed">
          No runs yet.
        </Text>
      ) : (
        <ExpandableContent
          shouldShowButton={hasMore}
          shouldShowDivider={false}
          showMoreLabel="Show more"
          showLessLabel="Show less"
        >
          {(isExpanded: boolean) => (
            <Stack gap="xs">
              {(isExpanded ? runs : runs.slice(0, RUNS_PREVIEW)).map((run) => (
                <Group key={run.id} justify="space-between">
                  <Text size="sm" c="dimmed">
                    Run {run.id} · {run.operation}
                  </Text>
                  <StatusTag status={run.status as WorkspaceStatus} />
                </Group>
              ))}
            </Stack>
          )}
        </ExpandableContent>
      )}
    </InformationCard>
  );
};

const groupType = (entity: string | null, domain: string | null): string => {
  if (entity !== null) return entity === 'benefit' ? 'Benefit' : entity;
  if (domain !== null) return domainLabel(domain);
  return 'Not classified yet';
};

const IntentText = styled.p`
  ${({ theme }) => css`
    margin: 0;
    color: ${theme.palette.neutral[80]};
    font-size: 0.8125rem;
    line-height: 1.5;
  `}
`;
